//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { FormEventHandler, ReactNode } from 'react';
import { Tab as MuiTab } from '@mui/material';
import styled from '@emotion/styled';

type TabProps = {
  label: string;
  description?: string;
  icon?: ReactNode;
  fullWidth?: boolean;
  id?: string;
  indicator?: boolean;
  onChange?: FormEventHandler<HTMLDivElement>;
  selected?: boolean;
  selectionFollowsFocus?: boolean;
  textColor?: string;
  value?: number;
};

const Tab = ({ label = '', description, icon, ...props }: TabProps) => {
  const tabLabel = () => (
    <div>
      <label style={{ pointerEvents: 'none' }}>{label}</label>
      <Description>{description}</Description>
    </div>
  );

  const renderTabLabel = () => {
    if (!icon) return tabLabel();
    else
      return (
        <IconWrapper>
          {icon}
          {tabLabel()}
        </IconWrapper>
      );
  };

  return <StyledTab label={renderTabLabel()} {...props} />;
};

export default Tab;

const StyledTab = styled(MuiTab)`
  min-width: 0;
  padding-top: ${({ theme }) => theme.spacing(2)};
  padding-bottom: ${({ theme }) => theme.spacing(2)};
  padding-left: ${({ theme }) => theme.spacing(2)};
  border-top-left-radius: ${({ theme }) => theme.shape.borderRadius};
  border-bottom-left-radius: ${({ theme }) => theme.shape.borderRadius};
  line-height: 1.5;

  &.Mui-selected {
    background: ${({ theme }) => theme.palette.background.default};

    & svg {
      opacity: 0.7;
      color: ${({ theme }) => theme.palette.primary.main};
    }
  }

  & .MuiTab-wrapper {
    align-items: flex-start;
    text-align: left;
  }
`;

const Description = styled.small`
  text-transform: none;
  display: block;
  font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
  color: ${({ theme }) => theme.palette.text.primary};
  opacity: 0.5;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;

  & svg {
    opacity: 0.3;
    width: 25px;
    height: 25px;
    margin-right: ${({ theme }) => theme.spacing(1.5)};
  }

  & label {
    pointer-events: none;
  }
`;
